import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { resolveOptionFromSearchQuery } from './searchable-select.helpers';

export interface SearchableSelectOption {
  value: string | number | null;
  label: string;
  disabled?: boolean;
}

const EMPTY_VALUES = new Set([
  null,
  undefined,
  '',
  -1,
  '-1',
  'null'
]);

function normalize(text: string): string {
  return text.trim().toUpperCase();
}

function isEmptyValue(value: unknown, emptyValue?: unknown): boolean {
  if (emptyValue !== undefined && value == emptyValue) {
    return true;
  }
  return EMPTY_VALUES.has(value as null);
}

@Component({
  selector: 'eview-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownEl') dropdownEl?: ElementRef<HTMLElement>;
  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholderKey = 'SEARCHABLE_SELECT_PLACEHOLDER';
  @Input() emptyMessageKey = 'SEARCHABLE_SELECT_EMPTY';
  @Input() emptyDisplayKey = 'PLEASE_SELECT';
  @Input() emptyValue: string | number | null = null;
  @Input() inputClass = 'form-control fontPrimaryMedium searchable-select__input';
  @Input() disabled = false;
  /** When false, option labels are shown as-is (e.g. form names). */
  @Input() translateLabels = true;
  /** When false, behaves like a compact picker (e.g. page size) — no filter typing on focus. */
  @Input() enableSearch = true;
  /** When false, hides the clear (×) control. */
  @Input() allowClear = true;
  /**
   * When false, options are not filtered client-side (server already filtered them).
   * Pair with searchQueryChange for on-demand parent/tag lists.
   */
  @Input() filterLocally = true;
  /**
   * When true, do not clear the control value if it is missing from options
   * (e.g. selected parent outside the current search page).
   */
  @Input() keepValueIfMissing = false;
  @Output() blurred = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<string | number | null>();
  /** Emits while the user types (enableSearch); use for server-side lookup. */
  @Output() searchQueryChange = new EventEmitter<string>();

  value: string | number | null = null;
  searchQuery = '';
  editing = false;
  showDropdown = false;

  filteredOptions: SearchableSelectOption[] = [];

  /** Viewport coords when dropdown is portaled to document.body. */
  dropdownStyle: {
    top: string;
    left: string;
    width: string;
    bottom: string;
    maxHeight: string;
  } | null = null;

  private onChange: (value: string | number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly boundReposition = () => this.updateDropdownPosition();
  private dropdownPortaled = false;

  constructor(
    private translate: TranslateService,
    private hostRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  get displayValue(): string {
    if (this.editing) {
      return this.searchQuery;
    }
    if (isEmptyValue(this.value, this.emptyValue)) {
      return this.translate.instant(this.emptyDisplayKey);
    }
    const selected = this.findOption(this.value);
    if (!selected) {
      return String(this.value ?? '');
    }
    return this.translateLabels
      ? this.translate.instant(selected.label)
      : selected.label;
  }

  get isPlaceholderState(): boolean {
    return isEmptyValue(this.value, this.emptyValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.updateFilteredOptions();
      this.clearValueIfNotInOptions();
    }
  }

  /** When the option list changes (e.g. parent filter), drop stale selections. */
  private clearValueIfNotInOptions(): void {
    if (this.keepValueIfMissing) {
      return;
    }
    if (isEmptyValue(this.value, this.emptyValue)) {
      return;
    }
    if (!this.findOption(this.value)) {
      this.value = this.emptyValue ?? null;
      this.searchQuery = '';
      this.editing = false;
      this.onChange(this.value);
      this.onTouched();
      this.selectionChange.emit(this.value);
    }
  }

  writeValue(value: string | number | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.closeDropdown();
    this.detachRepositionListeners();
  }

  onSearchInput(event: Event): void {
    if (!this.enableSearch) {
      return;
    }
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery = query;
    this.editing = true;
    this.showDropdown = true;
    this.searchQueryChange.emit(query);
    this.updateFilteredOptions();
    this.openDropdownOverlay();
  }

  onFocus(): void {
    if (this.disabled) {
      return;
    }
    if (!this.enableSearch) {
      this.editing = false;
      this.searchQuery = '';
      this.showDropdown = true;
      this.updateFilteredOptions();
      this.openDropdownOverlay();
      return;
    }
    this.editing = true;
    this.searchQuery = '';
    this.showDropdown = true;
    this.searchQueryChange.emit('');
    this.updateFilteredOptions();
    this.openDropdownOverlay();
  }

  onBlur(): void {
    window.setTimeout(() => {
      if (this.enableSearch && this.editing && this.searchQuery.trim()) {
        this.commitSearchQuery();
      }
      this.closeDropdown();
      this.onTouched();
      this.blurred.emit();
    }, 120);
  }

  /** Confirms typed text as selection when it matches one option. */
  private commitSearchQuery(): boolean {
    const match = resolveOptionFromSearchQuery(
      this.searchQuery,
      this.options,
      option => this.getOptionLabel(option)
    );
    if (!match) {
      return false;
    }
    this.selectOption(match);
    return true;
  }

  private getOptionLabel(option: SearchableSelectOption): string {
    return this.translateLabels
      ? this.translate.instant(option.label)
      : option.label;
  }

  selectOption(option: SearchableSelectOption): void {
    if (option.disabled) {
      return;
    }
    this.value = option.value;
    this.searchQuery = '';
    this.editing = false;
    this.closeDropdown();
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
  }

  clearSelection(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.value = this.emptyValue;
    this.searchQuery = '';
    this.editing = false;
    this.closeDropdown();
    this.onChange(this.value);
    this.onTouched();
    this.selectionChange.emit(this.value);
  }

  private openDropdownOverlay(): void {
    this.attachRepositionListeners();
    this.updateDropdownPosition();
    window.requestAnimationFrame(() => {
      if (!this.showDropdown) {
        return;
      }
      this.portalDropdownToBody();
      this.updateDropdownPosition();
    });
  }

  private closeDropdown(): void {
    this.showDropdown = false;
    this.editing = false;
    this.searchQuery = '';
    this.dropdownStyle = null;
    this.detachRepositionListeners();
    this.restoreDropdownToHost();
  }

  /** Body portal: fixed coords match viewport; avoids transform/overflow parent bugs. */
  private portalDropdownToBody(): void {
    const dropdown = this.dropdownEl?.nativeElement;
    if (!dropdown || dropdown.parentElement === document.body) {
      return;
    }
    this.renderer.appendChild(document.body, dropdown);
    this.dropdownPortaled = true;
  }

  private restoreDropdownToHost(): void {
    const dropdown = this.dropdownEl?.nativeElement;
    const host = this.hostRef.nativeElement;
    if (!dropdown || !this.dropdownPortaled || dropdown.parentElement === host) {
      this.dropdownPortaled = false;
      return;
    }
    this.renderer.appendChild(host, dropdown);
    this.dropdownPortaled = false;
  }

  private findOption(value: string | number | null): SearchableSelectOption | undefined {
    return this.options.find(option => option.value == value);
  }

  private updateDropdownPosition(): void {
    const el = this.inputEl?.nativeElement;
    if (!el || !this.showDropdown) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const maxHeightCap = 224;
    const gap = 4;
    const minSpace = 96;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < minSpace && spaceAbove > spaceBelow;
    const available = Math.max(64, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(maxHeightCap, available);

    if (openUp) {
      this.dropdownStyle = {
        top: 'auto',
        bottom: `${window.innerHeight - rect.top + gap}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${maxHeight}px`
      };
      return;
    }

    this.dropdownStyle = {
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${maxHeight}px`
    };
  }

  private attachRepositionListeners(): void {
    document.addEventListener('scroll', this.boundReposition, true);
    window.addEventListener('resize', this.boundReposition);
  }

  private detachRepositionListeners(): void {
    document.removeEventListener('scroll', this.boundReposition, true);
    window.removeEventListener('resize', this.boundReposition);
  }

  private updateFilteredOptions(): void {
    if (!this.filterLocally) {
      this.filteredOptions = [...this.options];
      if (this.showDropdown) {
        window.requestAnimationFrame(() => this.updateDropdownPosition());
      }
      return;
    }
    const query = normalize(this.searchQuery);
    if (!query) {
      this.filteredOptions = [...this.options];
      return;
    }
    this.filteredOptions = this.options.filter(option => {
      const labelText = this.getOptionLabel(option);
      const label = normalize(labelText);
      const valueText = normalize(String(option.value ?? ''));
      return label.includes(query) || valueText.includes(query);
    });
    if (this.showDropdown) {
      window.requestAnimationFrame(() => this.updateDropdownPosition());
    }
  }
}
